/**
 * useStudentPasswordState — « Mot de passe » : les trois champs, leur validation, l'envoi.
 *
 * Port d'`UpdatePasswordScreen.js` : le schéma (`validatePasswordChange`), `authService.updatePassword`
 * avec les trois champs. Au succès, l'accusé de l'ancien écran de félicitations (« Ton mot de passe a
 * bien été mis à jour ») s'affiche sur la page, et les champs se vident ; un refus met ses erreurs
 * sous leurs champs et son message au-dessus du bouton (l'ancien `ErrorTextM`). Le retour : l'écran
 * d'avant, ou les paramètres si la pile est vide (le « Retour » web d'aujourd'hui).
 */
import { useCallback, useReducer } from "react"
import { Keyboard } from "react-native"

import { authService } from "../../../../../../data/services/authService"
import {
    EMPTY_PASSWORD_CHANGE,
    passwordFieldErrors,
    toPasswordPayload,
    validatePasswordChange,
    type PasswordChangeErrors,
} from "../../../../adapters/student/Account/studentPasswordAdapter"
import { type UIPasswordChange } from "../../../types/studentAccount"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useBackOr } from "../../../hooks/useBackOr"

const PASSWORD_SAVED_MESSAGE = "Ton mot de passe a bien été mis à jour"
const PASSWORD_FAILED = "Ton mot de passe n'a pas pu être changé. Réessaie dans un instant."

export type StudentPasswordUiState = {
    values: UIPasswordChange
    errors: PasswordChangeErrors
    submitError: string | null
    savedMessage: string | null
}

export type StudentPasswordUiAction =
    | { type: "EDIT"; field: keyof UIPasswordChange; value: string }
    | { type: "INVALID"; errors: PasswordChangeErrors; submitError: string | null }
    | { type: "SUBMITTING" }
    | { type: "SAVED" }
    | { type: "DISMISS_SAVED" }

export const initialStudentPasswordUiState: StudentPasswordUiState = {
    values: EMPTY_PASSWORD_CHANGE,
    errors: {},
    submitError: null,
    savedMessage: null,
}

export function studentPasswordUiReducer(
    state: StudentPasswordUiState,
    action: StudentPasswordUiAction
): StudentPasswordUiState {
    switch (action.type) {
        case "EDIT": {
            const errors = { ...state.errors }
            delete errors[action.field]
            return { ...state, values: { ...state.values, [action.field]: action.value }, errors }
        }
        case "INVALID":
            return { ...state, errors: action.errors, submitError: action.submitError }
        case "SUBMITTING":
            return { ...state, errors: {}, submitError: null, savedMessage: null }
        case "SAVED":
            return { ...initialStudentPasswordUiState, savedMessage: PASSWORD_SAVED_MESSAGE }
        case "DISMISS_SAVED":
            return { ...state, savedMessage: null }
        default:
            return state
    }
}

export function useStudentPasswordState() {
    const onBack = useBackOr("settings")
    const [ui, dispatchUi] = useReducer(studentPasswordUiReducer, initialStudentPasswordUiState)

    const { start, requesting } = useApiMutation({
        service: authService.updatePassword,
        voice: "student",
        fallback: PASSWORD_FAILED,
        onSuccess: () => dispatchUi({ type: "SAVED" }),
        onError: (message, error) =>
            dispatchUi({ type: "INVALID", errors: passwordFieldErrors(error), submitError: message }),
    })

    const onSubmit = useCallback(() => {
        if (requesting) return
        const errors = validatePasswordChange(ui.values)
        if (Object.keys(errors).length > 0) {
            dispatchUi({ type: "INVALID", errors, submitError: null })
            return
        }
        Keyboard.dismiss()
        dispatchUi({ type: "SUBMITTING" })
        start({}, toPasswordPayload(ui.values))
    }, [requesting, ui.values, start])

    return {
        values: ui.values,
        errors: ui.errors,
        onChange: (field: keyof UIPasswordChange, value: string) => dispatchUi({ type: "EDIT", field, value }),
        onBack,
        onSubmit,
        submitting: requesting,
        submitError: ui.submitError,
        savedMessage: ui.savedMessage,
        onDismissSaved: () => dispatchUi({ type: "DISMISS_SAVED" }),
    }
}
